import { type Article } from "@lib/types";
import Image from "next/image";
import Link from "next/link";

import { Badge } from "@/components/ui/badge";
import { Container } from "@/components/ui/container";
import { urlForImageCached } from "@/sanity/lib/utils";

import { capitalizeFirst, formatDate } from "./utils";

interface ArticleHeadingProps {
  article: Omit<Article, "author"> & {
    author?: { _id: string; name: string | null } | null;
  };
  badge?: string | null;
  readTime?: string | null;
  authorCompanyHref?: string | null;
}

export function ArticleHeading({
  article,
  badge,
  readTime,
  authorCompanyHref,
}: ArticleHeadingProps) {
  const imageUrl = article.image?.image
    ? urlForImageCached(article.image.image)?.width(1920).url()
    : null;
  const imageAlt = article.image?.alt || article.title;

  const displayBadge = badge ?? capitalizeFirst(article._type);

  return (
    <section className="relative w-full">
      {/* Hero Image */}
      {imageUrl && (
        <div className="relative mx-auto aspect-video w-full max-w-[1920px]">
          <Image src={imageUrl} alt={imageAlt} fill className="object-cover" priority />
        </div>
      )}

      {/* Title and Metadata */}
      <Container className="relative z-10 pt-8 pb-8">
        <div className="flex flex-col gap-6">
          {/* Badge and Read Time */}
          {(badge !== undefined || readTime !== undefined) && (
            <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
              <Badge
                variant="outline"
                className="text-foreground font-header rounded-[100px] border-[1.5px] border-neutral-800 bg-transparent px-[11px] py-[2.5px] text-base md:text-lg"
              >
                {displayBadge}
              </Badge>
              {readTime && (
                <span className="text-foreground font-header text-base font-medium md:text-lg">
                  {readTime}
                </span>
              )}
            </div>
          )}

          {/* Title */}
          <h1 className="text-foreground font-header text-4xl leading-tight font-bold md:text-5xl lg:text-6xl">
            {article.title}
          </h1>

          {/* Metadata */}
          <div className="text-neutral flex flex-col gap-y-1 text-base md:text-lg">
            <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
              {badge === undefined && readTime === undefined && (
                <span className="font-medium">{capitalizeFirst(article._type)}</span>
              )}
              {article.publishedAt && (
                <>
                  {badge === undefined && readTime === undefined && (
                    <span className="text-neutral-400">•</span>
                  )}
                  <time dateTime={article.publishedAt} className="font-normal">
                    {formatDate(article.publishedAt)}
                  </time>
                </>
              )}
            </div>
            {article.author?.name &&
              (authorCompanyHref ? (
                <Link
                  href={authorCompanyHref}
                  className="text-inherit underline transition-colors hover:text-neutral-600"
                  {...(/^https?:\/\//i.test(authorCompanyHref)
                    ? { target: "_blank", rel: "noopener noreferrer" }
                    : {})}
                >
                  {article.author.name}
                </Link>
              ) : (
                <p className="font-normal">{article.author.name}</p>
              ))}
          </div>
        </div>
      </Container>
    </section>
  );
}
